import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { ApiError } from 'api/errors'
import {
  fetchMidasMessages,
  MidasConversation,
  MidasMessage,
  MidasMessagesPage,
  sendMidasFeedback,
  sendMidasMessage,
  startMidasConversation
} from 'api/midas'

// Poll fast while Midas is writing, slowly while waiting on the Goldie team.
export const STREAMING_POLL_MS = 700
export const HANDED_OVER_POLL_MS = 5000
// A reply still streaming after this long is treated as failed client-side,
// so a stuck worker can't poll forever. It outlasts the backend's own limits:
// the worker stops itself at 95s (TURN_DEADLINE_SECONDS) and GET …/messages
// reports a reply that hasn't grown for 120s as an error
// (STREAMING_STALE_AFTER). At 90s the website offered "Try again" while the
// backend still counted the reply as running, so the resend got a 409.
export const STREAMING_TIMEOUT_MS = 130_000

// Why POST …/messages answered 409 (api_router_midas._raise_for). The
// envelope has no code, so its message tells the two apart.
const CLOSED_CONFLICT = 'Conversation is closed'
const STILL_REPLYING_CONFLICT = 'Midas is still replying'

const HANDED_OVER_STATUSES = ['open', 'pending', 'solved']

type ConversationState = Pick<
  MidasConversation,
  'id' | 'status' | 'handed_over_at'
>

function mergeMessages(
  current: MidasMessage[],
  incoming: MidasMessage[]
): MidasMessage[] {
  if (incoming.length === 0) return current
  const byId = new Map(current.map(message => [message.id, message]))
  incoming.forEach(message => byId.set(message.id, message))
  return Array.from(byId.values()).sort((a, b) => a.id - b.id)
}

// Everything up to the first unfinished message is settled, so re-fetch from
// just before it; otherwise only ask for what is new.
function pollCursor(messages: MidasMessage[]): number {
  const streaming = messages.find(message => message.status === 'streaming')
  if (streaming) return streaming.id - 1
  return messages.length ? messages[messages.length - 1].id : 0
}

const CONVERSATION_KEY = ['midas', 'conversation']

export function useMidasChat(open: boolean) {
  const queryClient = useQueryClient()
  const conversationQuery = useQuery(CONVERSATION_KEY, startMidasConversation, {
    enabled: open,
    staleTime: Infinity,
    retry: false
  })
  const conversationId = conversationQuery.data?.id

  const [rawMessages, setMessages] = useState<MidasMessage[]>([])
  const [conversation, setConversation] = useState<ConversationState>()
  // The conversation whose history this mount has loaded. Every page mounts its
  // own panel, which starts from the cached conversation with no messages. Until
  // the history is in, the panel can't tell a new chat from one the team has,
  // so it shows "Connecting…" rather than a new chat's welcome and quick
  // replies (Cam's video, 5 Oct).
  const [historyFor, setHistoryFor] = useState<number>()
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string>()
  const [loadError, setLoadError] = useState(false)
  const [tick, setTick] = useState(0)
  const [catchUp, setCatchUp] = useState(0)
  const [closed, setClosed] = useState(false)
  const [timedOut, setTimedOut] = useState<ReadonlySet<number>>(new Set())
  const streamingSince = useRef(new Map<number, number>())

  const messages = useMemo(
    () =>
      timedOut.size === 0
        ? rawMessages
        : rawMessages.map(m =>
            m.status === 'streaming' && timedOut.has(m.id)
              ? { ...m, status: 'error' as const }
              : m
          ),
    [rawMessages, timedOut]
  )

  const messagesRef = useRef(messages)
  messagesRef.current = messages

  const applyPage = useCallback(
    (page: MidasMessagesPage) => {
      setMessages(current => mergeMessages(current, page.messages ?? []))
      const latest = page.conversation
      if (!latest) return
      setConversation(latest)
      // Keep the cached conversation current too, so the next page's panel
      // starts from where the chat stands rather than from the 'bot' snapshot
      // POST /midas/conversations returned.
      const cached =
        queryClient.getQueryData<MidasConversation>(CONVERSATION_KEY)
      if (
        cached?.id === latest.id &&
        (cached.status !== latest.status ||
          cached.handed_over_at !== latest.handed_over_at)
      ) {
        queryClient.setQueryData(CONVERSATION_KEY, { ...cached, ...latest })
      }
    },
    [queryClient]
  )

  useEffect(() => {
    if (conversationQuery.data) setConversation(conversationQuery.data)
  }, [conversationQuery.data])

  // Catch up whenever the panel opens (history on first open, team replies
  // that arrived while it was closed afterwards) or the customer retries.
  useEffect(() => {
    if (!open || !conversationId) return
    let cancelled = false
    fetchMidasMessages(conversationId, pollCursor(messagesRef.current))
      .then(page => {
        if (cancelled) return
        applyPage(page)
        setLoadError(false)
        setHistoryFor(conversationId)
      })
      .catch(() => !cancelled && setLoadError(true))
    return () => {
      cancelled = true
    }
  }, [open, conversationId, catchUp, applyPage])

  const historyLoaded =
    conversationId !== undefined && historyFor === conversationId

  const hasStreaming = messages.some(message => message.status === 'streaming')

  // Time out a reply that has been streaming (as first seen by this client)
  // for longer than STREAMING_TIMEOUT_MS.
  useEffect(() => {
    const now = Date.now()
    let nextDeadline: number | undefined
    messages.forEach(m => {
      if (m.status !== 'streaming') return
      if (!streamingSince.current.has(m.id)) {
        streamingSince.current.set(m.id, now)
      }
      const deadline =
        (streamingSince.current.get(m.id) ?? now) + STREAMING_TIMEOUT_MS
      nextDeadline = Math.min(nextDeadline ?? deadline, deadline)
    })
    if (nextDeadline === undefined) return
    const timer = setTimeout(() => {
      const expired = messagesRef.current.filter(
        m =>
          m.status === 'streaming' &&
          (streamingSince.current.get(m.id) ?? Infinity) +
            STREAMING_TIMEOUT_MS <=
            Date.now()
      )
      if (expired.length) {
        setTimedOut(current => {
          const next = new Set(current)
          expired.forEach(m => next.add(m.id))
          return next
        })
      }
    }, Math.max(0, nextDeadline - now))
    return () => clearTimeout(timer)
  }, [messages])
  const handedOver =
    !!conversation?.handed_over_at ||
    HANDED_OVER_STATUSES.includes(conversation?.status ?? '')

  const isClosed = closed || conversation?.status === 'closed'

  let pollDelay: number | undefined
  if (hasStreaming) pollDelay = STREAMING_POLL_MS
  else if (handedOver && !isClosed) pollDelay = HANDED_OVER_POLL_MS

  useEffect(() => {
    if (!open || !conversationId || !pollDelay) return
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const page = await fetchMidasMessages(
          conversationId,
          pollCursor(messagesRef.current)
        )
        if (!cancelled) {
          applyPage(page)
          // Fetched from this mount's own messages, so it is up to date even
          // if it beat the catch-up.
          setHistoryFor(conversationId)
        }
      } catch {
        // A missed poll is retried on the next tick.
      } finally {
        if (!cancelled) setTick(n => n + 1)
      }
    }, pollDelay)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [open, conversationId, pollDelay, tick, applyPage])

  const send = useCallback(
    async (body: string) => {
      const text = body.trim()
      if (!text || !conversationId) return false
      setSending(true)
      setSendError(undefined)
      try {
        const result = await sendMidasMessage(conversationId, text)
        const created = [result.customer_message, result.midas_message]
        setMessages(current =>
          mergeMessages(
            current,
            created.filter((m): m is MidasMessage => !!m)
          )
        )
        return true
      } catch (err) {
        const conflict =
          err instanceof ApiError && err.status === 409
            ? err.message
            : undefined
        if (conflict === CLOSED_CONFLICT) {
          // Only a closed conversation rejects new messages for good; the
          // customer can start a new chat (a solved one reopens to the team
          // server-side). The next page's panel starts from closed too.
          setClosed(true)
          const cached =
            queryClient.getQueryData<MidasConversation>(CONVERSATION_KEY)
          if (cached?.id === conversationId && cached.status !== 'closed') {
            queryClient.setQueryData(CONVERSATION_KEY, {
              ...cached,
              status: 'closed'
            })
          }
        } else if (conflict === STILL_REPLYING_CONFLICT) {
          // A reply is still running: one asked for in the app or another
          // tab, or one this client gave up on before the backend did. Read
          // it again and wait for it rather than show it as failed. The
          // customer's draft stays, to send once it is in.
          streamingSince.current.clear()
          setTimedOut(current => (current.size ? new Set() : current))
          setSendError('Midas is still replying. Please try again in a moment.')
          setCatchUp(n => n + 1)
        } else {
          setSendError("Your message didn't send. Please try again.")
          // A 409 in other words: read where the chat stands, so a closed
          // one still shows as closed.
          if (conflict !== undefined) setCatchUp(n => n + 1)
        }
        return false
      } finally {
        setSending(false)
      }
    },
    [conversationId, queryClient]
  )

  const startNewChat = useCallback(async () => {
    setSendError(undefined)
    try {
      const fresh = await startMidasConversation()
      queryClient.setQueryData(CONVERSATION_KEY, fresh)
      setConversation(fresh)
      setMessages([])
      setTimedOut(new Set())
      streamingSince.current.clear()
      setClosed(false)
    } catch {
      setSendError("We couldn't start a new chat. Please try again.")
    }
  }, [queryClient])

  const giveFeedback = useCallback(async (messageId: number, value: 1 | -1) => {
    const previous = messagesRef.current.find(m => m.id === messageId)
    const setFeedback = (feedback: 1 | -1 | null) =>
      setMessages(current =>
        current.map(m => (m.id === messageId ? { ...m, feedback } : m))
      )
    setFeedback(value)
    try {
      await sendMidasFeedback(messageId, value)
    } catch {
      setFeedback(previous?.feedback ?? null)
    }
  }, [])

  const retry = useCallback(() => {
    setLoadError(false)
    if (conversationQuery.isError) conversationQuery.refetch()
    else setCatchUp(n => n + 1)
  }, [conversationQuery])

  const lastCustomerBody = useMemo(
    () => [...messages].reverse().find(m => m.author_type === 'customer')?.body,
    [messages]
  )

  return {
    messages,
    conversation,
    handedOver: handedOver && !isClosed,
    closed: isClosed,
    historyLoaded,
    loading:
      conversationQuery.isLoading ||
      (!!conversationId && !historyLoaded && !loadError),
    error: conversationQuery.isError || loadError,
    replying: sending || hasStreaming,
    sendError,
    lastCustomerBody,
    send,
    startNewChat,
    giveFeedback,
    retry
  }
}
